<template>
    <div class="award-athletes-container">
        <v-row class="mb-4 mt-4">
            <v-col cols="12" md="5" lg="4">
                <v-text-field
                    v-model="search"
                    label="Search Athletes"
                    prepend-icon="mdi-magnify"
                    outlined
                    clearable
                    class="search-field"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="7" lg="8" class="d-flex flex-wrap justify-end action-buttons">
                <v-btn
                    v-if="false"
                    color="primary"
                    outlined
                    @click="skipCheckin"
                    :disabled="saving"
                    class="ma-1 py-5 px-4"
                    min-width="140"
                >
                    <v-icon left size="24">mdi-skip-next</v-icon>
                    Skip Check-in
                </v-btn>
                <v-btn
                    color="success"
                    outlined
                    @click="checkInAll(true)"
                    :disabled="saving"
                    class="ma-1 py-5 px-4 action-button"
                    min-width="140"
                >
                    <v-icon left size="24">mdi-check-all</v-icon>
                    Check In All
                </v-btn>
                <v-btn
                    color="error"
                    outlined
                    @click="checkInAll(false)"
                    :disabled="saving"
                    class="ma-1 py-5 px-4 action-button"
                    min-width="140"
                >
                    <v-icon left size="24">mdi-close-box-multiple</v-icon>
                    Clear All
                </v-btn>
            </v-col>
        </v-row>

        <v-card class="mb-4 summary-card" outlined>
            <v-card-text>
                <div class="d-flex align-center mb-2">
                    <v-icon color="info" class="mr-2">mdi-information-outline</v-icon>
                    <div class="text-subtitle-1 font-weight-medium">Check-in Status Summary</div>
                </div>

                <v-progress-linear
                    :value="overallCheckinPercentage"
                    height="10"
                    :color="overallCheckinColor"
                    class="mb-3"
                    rounded
                    striped
                >
                    <template v-slot:default="{ value }">
                        <strong>{{ Math.ceil(value) }}%</strong>
                    </template>
                </v-progress-linear>

                <div class="d-flex flex-wrap justify-space-between checkin-stats">
                    <v-chip class="ma-1" color="primary" outlined>
                        <v-icon left small>mdi-account-group</v-icon>
                        {{ totalAthletes }} Total Athletes
                    </v-chip>
                    <v-chip class="ma-1" color="success" outlined>
                        <v-icon left small>mdi-check-circle</v-icon>
                        {{ checkedInAthletes }} Checked In
                    </v-chip>
                    <v-chip class="ma-1" color="info" outlined>
                        <v-icon left small>mdi-format-list-checks</v-icon>
                        {{ completedCategories }} / {{ filteredResultCategories.length }} Categories Complete
                    </v-chip>
                </div>
            </v-card-text>
        </v-card>

        <v-expand-transition>
            <div v-if="overallCheckinPercentage === 100" class="all-checked-message mb-4 pa-3 text-center">
                <v-icon color="success" size="36" class="mb-2 pulse-animation">mdi-check-circle-outline</v-icon>
                <div class="text-h6 success--text">All Athletes Checked In!</div>
                <div class="text-subtitle-2 mt-1">You're ready to proceed to the Awards tab</div>
                <v-btn color="primary" class="mt-3" @click="skipCheckin">
                    <v-icon left>mdi-arrow-right-circle</v-icon>
                    Continue to Awards
                </v-btn>
            </div>
        </v-expand-transition>

        <v-expansion-panels v-model="openPanels" multiple class="athlete-panels">
            <v-expansion-panel
                v-for="(resultCategory, index) in filteredResultCategories"
                :key="`result-category-${resultCategory.id}`"
                class="mb-3 category-panel"
                :class="{
                    'complete-panel':
                        getCheckedInCount(resultCategory) === resultCategory.results.length &&
                        resultCategory.results.length > 0,
                }"
            >
                <v-expansion-panel-header class="panel-header">
                    <div class="d-flex align-center w-100">
                        <div class="mr-3">
                            <v-avatar size="36" :color="getCategoryStatus(resultCategory).bgColor" class="white--text">
                                <span>{{ getCheckedInCount(resultCategory) }}/{{ resultCategory.results.length }}</span>
                            </v-avatar>
                        </div>
                        <div class="category-info flex-grow-1">
                            <div class="d-flex align-center flex-wrap">
                                <div class="mr-4 py-2 d-flex align-center">
                                    <v-chip
                                        :color="getCategoryStatus(resultCategory).color"
                                        small
                                        class="mr-2 status-chip"
                                    >
                                        {{ getCategoryStatus(resultCategory).text }}
                                    </v-chip>
                                    <span class="category-title">{{ resultCategory.description() }}</span>
                                </div>
                                <v-spacer></v-spacer>
                                <div
                                    v-if="
                                        getCheckedInCount(resultCategory) === resultCategory.results.length &&
                                        resultCategory.results.length > 0
                                    "
                                    class="success--text d-flex align-center"
                                >
                                    <v-icon color="success" small class="mr-1 pulse-animation">mdi-check-circle</v-icon>
                                    <span>Complete</span>
                                </div>
                            </div>
                            <v-progress-linear
                                :value="(getCheckedInCount(resultCategory) / resultCategory.results.length) * 100"
                                height="6"
                                :color="getProgressColor(resultCategory)"
                                class="mt-1"
                                :indeterminate="saving && index === focusedCategoryIndex"
                                rounded
                            />
                        </div>
                    </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-data-table
                        :headers="headers"
                        :items="getResultsForCategory(resultCategory)"
                        :items-per-page="-1"
                        class="athlete-table elevation-1"
                        hide-default-footer
                        :loading="saving && index === focusedCategoryIndex"
                        :class="{
                            'complete-table':
                                getCheckedInCount(resultCategory) === resultCategory.results.length &&
                                resultCategory.results.length > 0,
                        }"
                    >
                        <template #item.placement="{ item: result }">
                            {{ result.latest_round_placement || '-' }}
                        </template>

                        <template #item.name="{ item: result }">
                            {{ result.athlete.name }}
                        </template>

                        <template #item.status="{ item: result }">
                            <v-tooltip bottom>
                                <template #activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        :color="result.award_prepared_at ? 'success' : 'grey'"
                                        :disabled="saving"
                                        icon
                                        x-large
                                        class="status-button"
                                        @click="toggleAthleteStatus(result, index)"
                                    >
                                        <v-icon size="32">{{
                                            result.award_prepared_at ? 'mdi-check-circle' : 'mdi-account-question'
                                        }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ result.award_prepared_at ? 'Present' : 'Not checked in' }}</span>
                            </v-tooltip>
                        </template>

                        <template #item.actions="{ item: result }">
                            <v-btn
                                :color="result.award_prepared_at ? 'error' : 'success'"
                                @click="toggleAthleteStatus(result, index)"
                                :disabled="saving"
                                class="action-button px-4 py-4 my-1"
                                min-width="120"
                            >
                                <v-icon left size="20">
                                    {{ result.award_prepared_at ? 'mdi-account-remove' : 'mdi-account-check' }}
                                </v-icon>
                                {{ result.award_prepared_at ? 'Mark Absent' : 'Mark Present' }}
                            </v-btn>
                        </template>

                        <template v-slot:no-data>
                            <div class="text-center pa-4">
                                <v-icon color="grey lighten-1" size="48" class="mb-2">mdi-account-off</v-icon>
                                <div class="text-subtitle-1 grey--text">No athletes found in this division</div>
                            </div>
                        </template>
                    </v-data-table>

                    <div
                        v-if="
                            getCheckedInCount(resultCategory) === resultCategory.results.length &&
                            resultCategory.results.length > 0
                        "
                        class="text-center complete-message mt-3 pa-2"
                    >
                        <div class="d-flex justify-center align-center">
                            <v-icon color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                            <span class="text-subtitle-1 success--text"
                                >All athletes in this division are checked in</span
                            >
                        </div>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" bottom>
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false" class="pa-3">
                    <v-icon left>mdi-close</v-icon>
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import Result from '@/Models/Result.js';

export default {
    name: 'AwardAthletes',

    props: {
        resultCategories: {
            type: Array,
            required: true,
        },
    },

    emits: ['saving', 'skip-checkin'],

    data: () => ({
        search: '',
        saving: false,
        openPanels: [], // Initially open all panels
        focusedCategoryIndex: null,
        snackbar: {
            show: false,
            text: '',
            color: 'info',
        },
        headers: [
            {
                text: 'Place',
                sortable: false,
                value: 'placement',
                width: '10%',
            },
            {
                text: 'Name',
                sortable: true,
                value: 'name',
                width: '40%',
            },
            {
                text: 'Status',
                sortable: false,
                value: 'status',
                width: '20%',
                align: 'center',
            },
            {
                text: 'Actions',
                sortable: false,
                value: 'actions',
                width: '30%',
                align: 'center',
            },
        ],
    }),

    computed: {
        filteredResultCategories() {
            if (!this.search) {
                return this.resultCategories;
            }

            // Filter the result categories that have athletes matching the search
            return this.resultCategories.filter((category) =>
                category.results.some((result) => {
                    const athleteName = result.athlete.name ? result.athlete.name.toLowerCase() : '';
                    return athleteName.includes(this.search.toLowerCase());
                }),
            );
        },

        // Calculate overall checkin progress
        totalAthletes() {
            return this.resultCategories.reduce((total, category) => total + category.results.length, 0);
        },

        checkedInAthletes() {
            return this.resultCategories.reduce(
                (total, category) => total + category.results.filter((result) => result.award_prepared_at).length,
                0,
            );
        },

        overallCheckinPercentage() {
            if (this.totalAthletes === 0) return 0;
            return Math.round((this.checkedInAthletes / this.totalAthletes) * 100);
        },

        overallCheckinColor() {
            if (this.overallCheckinPercentage === 100) return 'success';
            if (this.overallCheckinPercentage >= 75) return 'light-green';
            if (this.overallCheckinPercentage >= 50) return 'amber';
            if (this.overallCheckinPercentage >= 25) return 'orange';
            return 'blue-grey';
        },

        completedCategories() {
            let count = 0;

            for (const category of this.resultCategories) {
                if (this.getCheckedInCount(category) === category.results.length && category.results.length > 0) {
                    count++;
                }
            }

            return count;
        },
    },

    created() {
        // Initialize all panels to be open
        this.openPanels = this.resultCategories.map((_, index) => index);
    },

    methods: {
        getResultsForCategory(resultCategory) {
            if (!resultCategory || !resultCategory.results) {
                return [];
            }

            return resultCategory.results.filter((result) => {
                const athleteName = result.athlete?.name?.toLowerCase() || '';
                return athleteName.includes(this.search.toLowerCase());
            });
        },

        getCheckedInCount(resultCategory) {
            return resultCategory.results.filter((result) => result.award_prepared_at).length;
        },

        getCategoryStatus(resultCategory) {
            const checkedInCount = this.getCheckedInCount(resultCategory);
            const totalCount = resultCategory.results.length;

            if (checkedInCount === 0) {
                return { text: 'Not Started', color: 'grey', bgColor: 'grey darken-1' };
            }
            if (checkedInCount === totalCount) {
                return { text: 'Complete', color: 'success', bgColor: 'success' };
            }
            return { text: 'In Progress', color: 'warning', bgColor: 'amber darken-1' };
        },

        getProgressColor(resultCategory) {
            const checkedInCount = this.getCheckedInCount(resultCategory);
            const totalCount = resultCategory.results.length;

            if (checkedInCount === 0) return 'grey';
            if (checkedInCount === totalCount) return 'success';
            if (checkedInCount / totalCount > 0.7) return 'light-green';
            if (checkedInCount / totalCount > 0.3) return 'amber';
            return 'orange';
        },

        async toggleAthleteStatus(result, categoryIndex) {
            this.saving = true;
            this.focusedCategoryIndex = categoryIndex;
            this.$emit('saving', true);

            try {
                await Result.api().post('/api/awards', {
                    meet_id: this.resultCategories[0]?.meet_id,
                    model_type: 'App\\Models\\Result',
                    model_ids: [result.id],
                    field: 'award_prepared',
                    value: !result.award_prepared_at,
                });

                // Update the local state
                result.award_prepared_at = result.award_prepared_at ? null : new Date();

                this.snackbar = {
                    show: true,
                    text: `${result.athlete.name} marked as ${result.award_prepared_at ? 'present' : 'absent'}`,
                    color: result.award_prepared_at ? 'success' : 'info',
                };

                // Check if category is now complete
                const category = this.resultCategories[categoryIndex];
                if (this.getCheckedInCount(category) === category.results.length && category.results.length > 0) {
                    setTimeout(() => {
                        this.snackbar = {
                            show: true,
                            text: `All athletes in ${category.description()} are now checked in!`,
                            color: 'success',
                        };
                    }, 1000);
                }

                // Check if all athletes are checked in
                if (this.checkedInAthletes === this.totalAthletes && this.totalAthletes > 0) {
                    setTimeout(() => {
                        this.snackbar = {
                            show: true,
                            text: '🎉 All athletes are now checked in! You can proceed to Awards',
                            color: 'success',
                        };
                    }, 2000);
                }
            } catch (error) {
                console.error('Error updating athlete status:', error);
                this.snackbar = {
                    show: true,
                    text: 'Error updating athlete status',
                    color: 'error',
                };
            } finally {
                this.saving = false;
                this.focusedCategoryIndex = null;
                this.$emit('saving', false);
            }
        },

        async checkInAll(status) {
            if (this.resultCategories.length === 0) return;

            this.saving = true;
            this.$emit('saving', true);

            try {
                // Get all result IDs from all categories
                const allResultIds = this.resultCategories.reduce(
                    (ids, category) => [...ids, ...category.results.map((r) => r.id)],
                    [],
                );

                await Result.api().post('/api/awards', {
                    meet_id: this.resultCategories[0]?.meet_id,
                    model_type: 'App\\Models\\Result',
                    model_ids: allResultIds,
                    field: 'award_prepared',
                    value: status,
                });

                // Update local state for all results
                this.resultCategories.forEach((category) => {
                    category.results.forEach((result) => {
                        result.award_prepared_at = status ? new Date() : null;
                    });
                });

                this.snackbar = {
                    show: true,
                    text: status ? 'All athletes marked as present' : 'All athletes marked as absent',
                    color: status ? 'success' : 'info',
                };

                // If all athletes are now checked in, show a more prominent message
                if (status && this.totalAthletes > 0) {
                    setTimeout(() => {
                        this.snackbar = {
                            show: true,
                            text: '🎉 Check-in complete! You can now proceed to Awards',
                            color: 'success',
                        };
                    }, 1500);
                }
            } catch (error) {
                console.error('Error updating all athletes:', error);
                this.snackbar = {
                    show: true,
                    text: 'Error updating athlete statuses',
                    color: 'error',
                };
            } finally {
                this.saving = false;
                this.$emit('saving', false);
            }
        },

        skipCheckin() {
            this.snackbar = {
                show: true,
                text: 'Check-in process skipped. Proceed to Awards tab to continue.',
                color: 'info',
            };

            // Emit an event to signal the parent to switch to the Awards tab
            this.$emit('skip-checkin');
        },
    },
};
</script>

<style scoped>
.award-athletes-container {
    padding: 8px;
}

.search-field ::v-deep .v-input__slot {
    min-height: 56px;
}

.action-buttons {
    gap: 8px;
}

.athlete-panels ::v-deep .v-expansion-panel-header {
    padding: 16px;
    min-height: 64px;
}

.athlete-table {
    margin-top: 8px;
}

.athlete-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}

.athlete-table ::v-deep tbody tr {
    height: 64px !important;
    transition: background-color 0.25s ease;
}

.athlete-table ::v-deep tbody td {
    padding: 0 16px;
    height: 64px;
    font-size: 1rem;
}

.status-button ::v-deep .v-btn__content {
    height: 48px;
    width: 48px;
}

.action-button {
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.action-button:not(:disabled):hover {
    transform: scale(1.05);
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1) !important;
}

.summary-card {
    border-left: 4px solid var(--v-info-base);
    transition: all 0.3s ease;
}

.category-panel {
    transition: all 0.3s ease;
}

.complete-panel {
    position: relative;
}

.complete-panel ::v-deep .v-expansion-panel-header {
    background-color: rgba(76, 175, 80, 0.03);
}

.complete-table ::v-deep tbody {
    opacity: 0.9;
    background-color: rgba(76, 175, 80, 0.01);
}

.complete-message {
    border-radius: 4px;
    background-color: rgba(76, 175, 80, 0.03);
    border: 1px dashed rgba(76, 175, 80, 0.5);
}

.all-checked-message {
    border-radius: 8px;
    background-color: rgba(76, 175, 80, 0.03);
    border: 1px dashed rgba(76, 175, 80, 0.5);
}

.checkin-stats {
    flex-wrap: wrap;
}

.checkin-stats .v-chip {
    margin: 4px !important;
}

.category-title {
    font-weight: 500;
}

.status-chip {
    font-weight: 600;
}

.pulse-animation {
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.6;
    }
    100% {
        transform: scale(1);
        opacity: 0.8;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.5);
    }
    to {
        opacity: 0.9;
        transform: scale(1);
    }
}

/* iPad specific optimizations */
@media only screen and (min-width: 768px) and (max-width: 1024px) {
    .athlete-table ::v-deep tbody td {
        height: 72px;
        padding: 0 12px;
    }

    .action-button {
        min-height: 54px;
    }
}

/* iPad landscape orientation */
@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    .award-athletes-container {
        padding: 12px;
    }
}

/* iPad portrait orientation */
@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    .award-athletes-container {
        padding: 16px 8px;
    }

    .action-buttons {
        justify-content: flex-start !important;
        padding-top: 8px;
    }
}
</style>
